import React, { useState } from 'react';

// Seller deal details, grouped into sections. Each section has its own Edit button, like the buyer sections.
// Listing status is shown but not editable here: the agent changes it with the "Has this listing gone live on the MLS?" task.
const fmtLongDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};
const money = (v) => (v ? `$${Number(String(v).replace(/[^0-9.]/g, '')).toLocaleString()}` : '');
const LISTING_STATUS_LABEL = { live: 'Live', pending: 'Pending / Under Contract', incoming: 'Incoming' };
const yesNoOptions = [['Yes', 'Yes'], ['No', 'No']];

const buildSections = () => [
  {
    title: 'Listing Information',
    fields: [
      { key: 'listingPrice', label: 'Listing price', type: 'money' },
      { key: 'listingStatus', label: 'Listing status', type: 'readonly', display: (v) => LISTING_STATUS_LABEL[v] || 'Incoming' },
      { key: 'anticipatedListingDate', label: 'Anticipated listing date', type: 'date', sortable: true },
      { key: 'liveDate', label: 'Date listed live on MLS', type: 'date', sortable: true, hint: 'Entering a date marks the listing Live and closes the MLS question.' },
      { key: 'listingEffectiveDate', label: 'Listing agreement effective date', type: 'date', sortable: true },
      { key: 'listingExpirationDate', label: 'Listing expiration date', type: 'date', sortable: true },
      { key: 'photographyDate', label: 'Photography date', type: 'date', sortable: true },
      { key: 'desiredClosingDate', label: 'Desired closing date', type: 'date', sortable: true },
      { key: 'reasonForSelling', label: 'Reason for selling', type: 'textarea' },
    ],
  },
  {
    title: 'Staging & Photography',
    fields: [
      { key: 'stagingNeeded', label: 'Staging needed', type: 'text' },
      { key: 'stagingNotes', label: 'Staging notes', type: 'textarea' },
      { key: 'videoScriptNotes', label: 'Video script notes', type: 'textarea' },
    ],
  },
  {
    title: 'Marketing Materials',
    fields: [
      { key: 'createBrochure', label: 'Create brochure', type: 'bool' },
      { key: 'createMailer', label: 'Create mailer', type: 'bool' },
      { key: 'createJustSoldPostcards', label: 'Just sold postcards', type: 'bool' },
      { key: 'createFeatureCards', label: 'Create feature cards', type: 'bool' },
      { key: 'marketingTheme', label: 'Marketing theme', type: 'text' },
      { key: 'keyFeatures', label: 'Key features', type: 'textarea' },
    ],
  },
  {
    title: 'Showing Instructions',
    fields: [
      { key: 'showingInstructions', label: 'Showing instructions', type: 'textarea' },
      { key: 'accessInstructions', label: 'Access instructions', type: 'textarea' },
      { key: 'lockboxCode', label: 'Lockbox code', type: 'text' },
    ],
  },
];

const displayValue = (tx, f) => {
  const raw = tx[f.key];
  if (f.display) return f.display(raw);
  if (f.type === 'date') return fmtLongDate(raw);
  if (f.type === 'money') return money(raw);
  if (f.type === 'bool') return raw === true || raw === 'Yes' ? 'Yes' : raw === false || raw === 'No' ? 'No' : '';
  return raw == null ? '' : String(raw);
};

const styles = {
  section: { background: '#FFFFFF', border: '1px solid #D5D9E0', borderRadius: 8, padding: 16, marginBottom: 16 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #D4AF37', paddingBottom: 8, marginBottom: 12, gap: 8, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a1a' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 },
  label: { fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8C6D1F', fontWeight: 700, display: 'block', marginBottom: 4 },
  value: { fontSize: 15, color: '#1a1a1a', wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1a1a1a', background: '#FFFFFF' },
  gold: { padding: '6px 12px', borderRadius: 6, border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  ghost: { padding: '6px 12px', borderRadius: 6, border: '1px solid #D5D9E0', background: '#FFFFFF', color: '#5B6270', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  note: { fontSize: 12, color: '#5B6270', marginTop: 4 },
  error: { color: '#B42323', fontSize: 13, marginTop: 8 },
};

function Section({ section, tx, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Date fields in this section are listed in date order when viewing; other fields keep their order first.
  const viewFields = [
    ...section.fields.filter((f) => !f.sortable),
    ...section.fields.filter((f) => f.sortable).sort((a, b) => (tx[a.key] || '￿').localeCompare(tx[b.key] || '￿')),
  ];
  const editableFields = section.fields.filter((f) => f.type !== 'readonly');

  const startEdit = () => {
    const d = {};
    editableFields.forEach((f) => {
      const raw = tx[f.key];
      d[f.key] = f.type === 'bool' ? (raw === true || raw === 'Yes' ? 'Yes' : raw === false || raw === 'No' ? 'No' : '') : (raw ?? '');
    });
    setDraft(d);
    setError('');
    setEditing(true);
  };

  const save = async () => {
    const updates = {};
    editableFields.forEach((f) => {
      const v = draft[f.key] ?? '';
      if (f.type === 'bool') updates[f.key] = v === 'Yes';
      else updates[f.key] = v;
    });
    try {
      setBusy(true);
      await onSave(updates);
      setEditing(false);
    } catch (err) {
      console.error('Could not save seller section', err);
      setError('Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const renderInput = (f) => {
    const v = draft[f.key] ?? '';
    const set = (val) => setDraft({ ...draft, [f.key]: val });
    if (f.type === 'bool') {
      return (
        <select value={v} onChange={(e) => set(e.target.value)} style={styles.input}>
          <option value="">Choose</option>
          {yesNoOptions.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
        </select>
      );
    }
    if (f.type === 'textarea') {
      return <textarea value={v} onChange={(e) => set(e.target.value)} rows={3} style={styles.input} />;
    }
    if (f.type === 'money') {
      return <input type="text" inputMode="decimal" value={v} onChange={(e) => set(e.target.value)} placeholder="$0" style={styles.input} />;
    }
    return <input type={f.type === 'date' ? 'date' : 'text'} value={v} onChange={(e) => set(e.target.value)} style={styles.input} />;
  };

  return (
    <div style={styles.section}>
      <div style={styles.head}>
        <h3 style={styles.title}>{section.title}</h3>
        {!editing ? (
          <button type="button" onClick={startEdit} style={styles.ghost}>Edit</button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={save} disabled={busy} style={styles.gold}>{busy ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} style={styles.ghost}>Cancel</button>
          </div>
        )}
      </div>
      {!editing ? (
        <div style={styles.grid}>
          {viewFields.map((f) => (
            <div key={f.key}>
              <span style={styles.label}>{f.label}</span>
              <div style={styles.value}>{displayValue(tx, f) || <span style={{ color: '#9AA1AD' }}>Not set</span>}</div>
              {f.type === 'readonly' && <div style={styles.note}>Changes when a live date is entered or the agent answers the MLS live question.</div>}
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.grid}>
          {section.fields.map((f) => (
            <div key={f.key}>
              <span style={styles.label}>{f.label}</span>
              {f.type === 'readonly' ? (
                <div style={styles.value}>{displayValue(tx, f)}</div>
              ) : renderInput(f)}
              {f.hint && <div style={styles.note}>{f.hint}</div>}
            </div>
          ))}
        </div>
      )}
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

export default function SellerDetailSections({ transaction, onSave }) {
  return (
    <>
      {buildSections().map((section) => (
        <Section key={section.title} section={section} tx={transaction} onSave={onSave} />
      ))}
    </>
  );
}
